const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/components/ErrorBoundary/ErrorBoundary.tsx';
import * as ReactRedux from 'react-redux';
import { Button } from '@mui/material';
import { showSnackbar } from '../../actions/uiActions';
import { ErrorDlg } from './ErrorDlg';
import { Logger } from '../../services/KisLoggerService';
import { pushLog } from '../../actions/logActions';
import { ErrorInfo, PureComponent, ReactNode } from 'react';

const Log = Logger('ErrorBoundary');

/**
 * Interface for the optional action buttons
 */
export interface IActionBtn {
    label: string;
    onClick: () => void;
}

/**
 * React props
 */
export interface IErrorBoundaryProps {
    /**
     * Set this property if a static error message should be displayed when an error occurs, e.g.
     * 'An unexpected error occurred. Please contact administration staff'. You can also set a React
     * element here, e.g. if your static message should include an icon, etc.
     */
    fallback?: string | ReactNode;

    /**
     * Set this property if you want to dynamically generate the error text and/or component at the time
     * when the error occurs, e.g. if you want to include the exception message in the rendered text.
     */
    getDynamicFallback?: (err: Error) => string | ReactNode;

    /**
     * Set this property to pass additional info (such as the component's props) that should be included in the log.
     */
    logExtras?: unknown;

    /**
     * true=also show a snackbar
     */
    shouldShowSnackbar?: boolean;

    /**
     * Use this property to derive a snackbar message from an error. If you do not provide this callback
     * and set shouldShowSnackbar to true, the message will consist of the untranslated exception message.
     */
    getSnackbarText?: (err: Error) => string;

    /**
     * true=also show a dialogue
     */
    shouldShowDialog?: boolean;

    /**
     * Use this property to derive a dialogue message from an error. If you do not provide this callback
     * and set shouldShowDialog to true, the message will consist of the untranslated exception message.
     */
    getDialogText?: (err: Error) => string;

    /**
     * Optional action buttons. If shouldShowDialog is set, these buttons will appear inside the dialog,
     * otherwise they will appear within the fallback component.
     */
    actionBtns?: IActionBtn[];

    /**
     * Nested components
     */
    children: any;
}

/**
 * Mapped actions
 */
interface IMapDispatchToProps {
    showSnackbar: typeof showSnackbar;
    pushLog: typeof pushLog;
}

/**
 * Actions mapper
 */
const mapDispatchToProps: IMapDispatchToProps = {
    showSnackbar,
    pushLog: pushLog,
};

/**
 * React props including injected props
 */
type IErrorBoundaryImplProps = IErrorBoundaryProps & IMapDispatchToProps;

/**
 * React state
 */
interface IState {
    error: Error | null;
    isDlgOpen: boolean;
}

/**
 * React component
 */
class ErrorBoundaryImpl extends PureComponent<IErrorBoundaryImplProps, IState> {
    constructor(props: IErrorBoundaryImplProps) {
        super(props);
        this.state = { error: null, isDlgOpen: false };
    }

    static getDerivedStateFromError(error: Error): IState {
        return {
            error,
            isDlgOpen: true, // even if props.shouldShowDialog is false; can't access props from here!
        };
    }

    public override componentDidCatch(err: Error, info: ErrorInfo) {
        Log.error(`componentDidCatch: ${err.message}\n${info.componentStack}\n${err.stack}`);

        this.props.pushLog('error', err.message, {
            location: Log.channel,
            logExtras: this.props.logExtras,
            // The componentStack may not be useful in production when the bundle is minified.
            stack: [...(info.componentStack || '').split('\n').slice(0, 8), '    [...]'].join('\n'),
        });

        if (this.props.shouldShowSnackbar) {
            try {
                const snackMsg = (() => {
                    if (this.props.getSnackbarText) {
                        return this.props.getSnackbarText(err);
                    } else {
                        return this.getDefaultMessage(err);
                    }
                })();
                this.props.showSnackbar(snackMsg);
            } catch (e) {
                Log.error(`Exception while trying to build snackbar text!`, e);
            }
        }
    }

    public getDefaultMessage(err: Error): string {
        return (err?.message || '?').trim().split('\n')[0];
    }

    public override render() {
        if (!this.state.error) {
            return this.props.children;
        }

        return (
            <>
                {this.renderFallbackContent()}
                {this.props.actionBtns && !this.props.shouldShowDialog && this.renderInlineActionBtns()}
                {this.props.shouldShowDialog && this.renderDialog()}
            </>
        );
    }

    private renderFallbackContent(): string | ReactNode | null {
        if (!this.state.error) {
            return null;
        }

        if (this.props.fallback) {
            return this.props.fallback;
        }

        if (this.props.getDynamicFallback) {
            try {
                return this.props.getDynamicFallback(this.state.error) || null;
            } catch (e) {
                Log.error(`getDynamicFallback ran into a problem!`, e);
                return null;
            }
        }

        return null;
    }

    private renderInlineActionBtns(): ReactNode {
        const btns = this.props.actionBtns?.map((actionBtn: IActionBtn, idx: number) => (
            <Button key={idx} color="primary" variant="text" onClick={actionBtn.onClick}>
                {actionBtn.label}
            </Button>
        ));
        return <>{btns}</>;
    }

    private renderDialog(): ReactNode | null {
        if (!this.state.error) {
            return null;
        }
        const msg = (() => {
            if (this.props.getDialogText) {
                return this.props.getDialogText(this.state.error);
            } else {
                return this.getDefaultMessage(this.state.error);
            }
        })();
        return (
            <ErrorDlg
                msg={msg}
                isOpen={this.state.isDlgOpen}
                closeDlg={this.closeDialog}
                actionBtns={this.props.actionBtns}
            />
        );
    }

    private closeDialog = () => {
        this.setState({ isDlgOpen: false });
    };
}

export const ErrorBoundary = ReactRedux.connect(null, mapDispatchToProps)(ErrorBoundaryImpl);
